<script setup>
import { ref, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import FunnelAPI from 'dashboard/api/funnels';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'next/avatar/Avatar.vue';

const props = defineProps({
  funnel: { type: Object, required: true },
});

const emit = defineEmits(['close']);
const { t } = useI18n();
const store = useStore();

const permissions = ref([]);
const loading = ref(false);
const newType = ref('User');
const newId = ref(null);
const newAccess = ref('read');

const agents = useMapGetter('agents/getAgents');
const teams = useMapGetter('teams/getMyTeams');

onMounted(async () => {
  store.dispatch('agents/get');
  store.dispatch('teams/get');
  await fetchPermissions();
});

const fetchPermissions = async () => {
  loading.value = true;
  try {
    const { data } = await FunnelAPI.getPermissions(props.funnel.id);
    permissions.value = data;
  } catch {
    useAlert(t('CRM.PERMISSIONS.FETCH_ERROR'));
  }
  loading.value = false;
};

const assignableList = computed(() => {
  if (newType.value === 'User') return agents.value;
  return teams.value;
});

const addPermission = async () => {
  if (!newId.value) return;
  try {
    await FunnelAPI.createPermission(props.funnel.id, {
      funnel_permission: {
        permissionable_type: newType.value,
        permissionable_id: newId.value,
        access_level: newAccess.value,
      },
    });
    useAlert(t('CRM.PERMISSIONS.ADDED'));
    newId.value = null;
    await fetchPermissions();
  } catch {
    useAlert(t('CRM.PERMISSIONS.ADD_ERROR'));
  }
};

const updatePermission = async (permId, accessLevel) => {
  try {
    await FunnelAPI.updatePermission(props.funnel.id, permId, {
      funnel_permission: { access_level: accessLevel },
    });
    await fetchPermissions();
  } catch {
    useAlert(t('CRM.PERMISSIONS.UPDATE_ERROR'));
  }
};

const removePermission = async permId => {
  try {
    await FunnelAPI.deletePermission(props.funnel.id, permId);
    useAlert(t('CRM.PERMISSIONS.REMOVED'));
    await fetchPermissions();
  } catch {
    useAlert(t('CRM.PERMISSIONS.REMOVE_ERROR'));
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="`${t('CRM.PERMISSIONS.TITLE')} - ${funnel.name}`"
    />

    <div class="w-full">
      <!-- Add permission form -->
      <div
        class="flex items-end gap-2 mb-4 p-3 bg-n-alpha-1 rounded-lg"
      >
        <label class="flex-shrink-0">
          <span class="text-xs font-medium text-n-slate-10 mb-1 block">
            {{ t('CRM.PERMISSIONS.TYPE') }}
          </span>
          <select
            v-model="newType"
            class="w-auto rounded-lg border border-n-weak bg-n-background px-2 py-1.5 text-sm text-n-slate-12"
          >
            <option value="User">
              {{ t('CRM.PERMISSIONS.AGENT') }}
            </option>
            <option value="Team">
              {{ t('CRM.PERMISSIONS.TEAM') }}
            </option>
          </select>
        </label>
        <label class="flex-1 min-w-0">
          <span class="text-xs font-medium text-n-slate-10 mb-1 block">
            {{ t('CRM.PERMISSIONS.ASSIGNEE') }}
          </span>
          <select
            v-model="newId"
            class="w-full rounded-lg border border-n-weak bg-n-background px-2 py-1.5 text-sm text-n-slate-12"
          >
            <option :value="null" disabled>
              {{ t('CRM.PERMISSIONS.SELECT') }}
            </option>
            <option
              v-for="item in assignableList"
              :key="item.id"
              :value="item.id"
            >
              {{ item.name }}
            </option>
          </select>
        </label>
        <label class="flex-shrink-0">
          <span class="text-xs font-medium text-n-slate-10 mb-1 block">
            {{ t('CRM.PERMISSIONS.ACCESS') }}
          </span>
          <select
            v-model="newAccess"
            class="w-auto rounded-lg border border-n-weak bg-n-background px-2 py-1.5 text-sm text-n-slate-12"
          >
            <option value="read">
              {{ t('CRM.PERMISSIONS.READ') }}
            </option>
            <option value="edit">
              {{ t('CRM.PERMISSIONS.EDIT') }}
            </option>
          </select>
        </label>
        <Button
          size="sm"
          :label="t('CRM.PERMISSIONS.ADD')"
          @click="addPermission"
        />
      </div>

      <!-- Loading -->
      <div
        v-if="loading"
        class="flex flex-col gap-2 py-4"
      >
        <div
          v-for="i in 3"
          :key="i"
          class="h-12 bg-n-slate-3 animate-pulse rounded-lg"
        />
      </div>

      <!-- Empty -->
      <div
        v-else-if="permissions.length === 0"
        class="flex flex-col items-center py-8 gap-2"
      >
        <span class="i-lucide-shield-off size-8 text-n-slate-7" />
        <span class="text-sm text-n-slate-10">
          {{ t('CRM.PERMISSIONS.EMPTY') }}
        </span>
      </div>

      <!-- Permission list -->
      <div v-else class="flex flex-col gap-1">
        <div
          v-for="perm in permissions"
          :key="perm.id"
          class="flex items-center justify-between py-2.5 px-2 -mx-2 gap-2 rounded-lg hover:bg-n-alpha-1 transition-colors"
        >
          <div class="flex items-center gap-2.5 min-w-0">
            <Avatar
              :name="perm.permissionable_name"
              :size="28"
              rounded-full
            />
            <div class="min-w-0">
              <span class="text-sm text-n-slate-12 truncate block">
                {{ perm.permissionable_name }}
              </span>
              <span
                class="text-[11px] px-1.5 py-0.5 rounded-full bg-n-alpha-2 text-n-slate-10"
              >
                {{
                  perm.permissionable_type === 'User'
                    ? t('CRM.PERMISSIONS.AGENT')
                    : t('CRM.PERMISSIONS.TEAM')
                }}
              </span>
            </div>
          </div>
          <div class="flex items-center gap-2 flex-shrink-0">
            <select
              :value="perm.access_level"
              class="text-sm w-auto rounded-lg border border-n-weak bg-n-background px-2 py-1 text-n-slate-12"
              @change="updatePermission(perm.id, $event.target.value)"
            >
              <option value="read">
                {{ t('CRM.PERMISSIONS.READ') }}
              </option>
              <option value="edit">
                {{ t('CRM.PERMISSIONS.EDIT') }}
              </option>
            </select>
            <button
              class="size-7 grid place-content-center rounded-md hover:bg-n-ruby-2 transition-colors"
              @click="removePermission(perm.id)"
            >
              <span class="i-lucide-trash-2 size-3.5 text-n-slate-10 hover:text-n-ruby-11" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="flex justify-end py-2 mt-2">
      <Button
        faded
        slate
        :label="t('CRM.PERMISSIONS.CLOSE')"
        @click="emit('close')"
      />
    </div>
  </div>
</template>
